import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import type { TaskRow } from "~/api/opsTypes";
import {
  useApproveRun,
  useCancelDispatch,
  useControlRun,
  useRuns,
  useRunTaskNow,
  useSetRunBudget,
  useSetTaskPaused,
  useStepRun,
  useStopRun,
  useTaskBoard,
} from "~/api/queries";
import type { Run } from "~/api/types";
import { ActionLedger } from "~/features/automation/ActionLedger";
import { BandStrip, HealthPill, SummaryStat } from "~/features/automation/components";
import { compactTokens, formatShare } from "~/features/automation/format";
import { Select } from "~/features/settings/controls";
import { Button } from "~/foundation/components/Button";
import { Empty } from "~/foundation/components/Empty";
import { Spinner } from "~/foundation/components/Spinner";
import { Text } from "~/foundation/components/Text";
import { cn } from "~/foundation/utils/cn";
import { formatRelativeTime } from "~/foundation/utils/time";

/** Non-terminal states across all three sources — the "still moving" set. */
const LIVE_STATES = new Set(["running", "observing", "requested", "claimed", "queued", "active"]);

function isLive(row: TaskRow): boolean {
  return LIVE_STATES.has(row.state);
}

const SOURCE_LABELS: Record<string, string> = {
  scheduled: "Scheduled",
  run: "Run",
  dispatch: "Dispatch",
};

function rowKey(row: TaskRow): string {
  return `${row.source}-${row.id}`;
}

/** The 7-column row shared by every band's table. */
function TaskTableRow({
  row,
  runDto,
  onOpenLedger,
  approvalBusyId,
  onApprove,
}: {
  row: TaskRow;
  runDto: Run | undefined;
  onOpenLedger: (row: TaskRow) => void;
  approvalBusyId: string | null;
  onApprove: (run: Run, approved: boolean) => void;
}) {
  const { t, i18n } = useTranslation();
  const setPaused = useSetTaskPaused();
  const runNow = useRunTaskNow();
  const control = useControlRun();
  const stop = useStopRun();
  const step = useStepRun();
  const cancelDispatch = useCancelDispatch();
  const setBudget = useSetRunBudget();

  const scheduleText = row.schedule?.nextFireAt ? (
    <>
      {t("next")} {formatRelativeTime(row.schedule.nextFireAt, i18n.language)}
    </>
  ) : row.schedule?.lastFiredAt ? (
    <>
      {t("last fired")} {formatRelativeTime(row.schedule.lastFiredAt, i18n.language)}
    </>
  ) : row.schedule?.expiresAt ? (
    <>
      {t("expires")} {formatRelativeTime(row.schedule.expiresAt, i18n.language)}
    </>
  ) : row.lastActionAt ? (
    <>
      {t("last action")} {formatRelativeTime(row.lastActionAt, i18n.language)}
    </>
  ) : (
    "—"
  );

  const renderActions = () => {
    if (row.source === "scheduled") {
      const decisionId = row.links.decisionId;
      if (!decisionId) {
        return <span className="text-small text-text-tertiary">{t("no decision")}</span>;
      }
      return (
        <div className="flex flex-wrap gap-1.5">
          {row.state === "active" ? (
            <Button size="sm" onClick={() => setPaused.mutate({ id: decisionId, paused: true })}>
              {t("Pause")}
            </Button>
          ) : (
            <Button size="sm" onClick={() => setPaused.mutate({ id: decisionId, paused: false })}>
              {t("Resume")}
            </Button>
          )}
          <Button
            size="sm"
            onClick={() =>
              runNow.mutate(decisionId, {
                onSuccess: (result) => {
                  toast.success(t("Run started"));
                  if (result.note) {
                    toast.message(result.note);
                  }
                },
              })
            }
          >
            {t("Run now")}
          </Button>
        </div>
      );
    }

    if (row.source === "run") {
      const terminal = ["completed", "failed", "cancelled"].includes(row.state);
      const awaiting = runDto?.awaitingApprovalNodeId;
      return (
        <div className="flex flex-wrap gap-1.5">
          {awaiting && runDto ? (
            <>
              <Button
                size="sm"
                disabled={approvalBusyId === runDto.id}
                title={t('Approve node "{{node}}" and release the run', { node: awaiting })}
                onClick={() => onApprove(runDto, true)}
              >
                ✓ {t("Approve")}
              </Button>
              <Button
                size="sm"
                variant="danger"
                disabled={approvalBusyId === runDto.id}
                title={t('Deny node "{{node}}"', { node: awaiting })}
                onClick={() => onApprove(runDto, false)}
              >
                ✕ {t("Deny")}
              </Button>
            </>
          ) : null}
          {!terminal && row.state === "running" ? (
            <Button size="sm" onClick={() => control.mutate({ id: row.id, action: "pause" })}>
              {t("Pause")}
            </Button>
          ) : null}
          {!terminal && row.state === "paused" ? (
            <Button size="sm" onClick={() => control.mutate({ id: row.id, action: "resume" })}>
              {t("Resume")}
            </Button>
          ) : null}
          {!terminal ? (
            <>
              {row.target?.canStep ? (
                <Button size="sm" onClick={() => step.mutate({ id: row.id })}>
                  {row.target?.stepMode ? t("Step (1 node)") : t("Step")}
                </Button>
              ) : null}
              {row.target?.stepMode ? (
                <Button size="sm" onClick={() => step.mutate({ id: row.id, stop: true })}>
                  {t("Leave step mode")}
                </Button>
              ) : null}
              <Button size="sm" onClick={() => stop.mutate({ id: row.id, mode: "graceful" })}>
                {t("Stop after node")}
              </Button>
              <Button
                size="sm"
                variant="danger"
                onClick={() => {
                  if (
                    window.confirm(
                      t(
                        "Abort now marks the run cancelled immediately. The current node stops counting as in-progress. Continue?",
                      ),
                    )
                  ) {
                    stop.mutate({ id: row.id, mode: "immediate" });
                  }
                }}
              >
                {t("Abort now")}
              </Button>
            </>
          ) : null}
          <Button size="sm" onClick={() => onOpenLedger(row)}>
            {t("Ledger")}
          </Button>
          <Button
            size="sm"
            onClick={() => {
              const raw = window.prompt(
                t("Token ceiling for this run (blank clears it):"),
                String(row.spend?.budget ?? 10_000_000),
              );
              if (raw === null) {
                return;
              }
              const trimmed = raw.trim();
              setBudget.mutate({
                runId: row.id,
                tokenBudget: trimmed === "" ? null : Number(trimmed),
              });
            }}
          >
            {t("Budget")}
          </Button>
        </div>
      );
    }

    return (
      <div className="flex flex-wrap gap-1.5">
        <Button size="sm" onClick={() => cancelDispatch.mutate({ id: row.id })}>
          {t("Cancel")}
        </Button>
        {row.links.run ? (
          <Button size="sm" onClick={() => onOpenLedger(row)}>
            {t("Ledger")}
          </Button>
        ) : null}
      </div>
    );
  };

  return (
    <tr className="border-b border-divider last:border-b-0 align-top">
      <td className="px-3 py-2.5">
        <p className="font-semibold text-body text-text">{row.title}</p>
        <p className="text-small text-text-tertiary">
          {t(SOURCE_LABELS[row.source] ?? row.source)} · {row.kind}
        </p>
      </td>
      <td className="px-3 py-2.5">
        <span
          className={cn(
            "inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-small font-semibold capitalize",
            isLive(row) ? "bg-accent/15 text-accent" : "bg-bg-secondary text-text-secondary",
          )}
        >
          {isLive(row) ? (
            <span className="size-1.5 shrink-0 animate-pulse rounded-full bg-current" aria-hidden />
          ) : null}
          {row.state}
        </span>
        <p className="mt-0.5 text-small text-text-secondary">{scheduleText}</p>
      </td>
      <td className="px-3 py-2.5">
        <HealthPill level={row.health.level} note={row.health.note} />
      </td>
      <td className="px-3 py-2.5">
        {row.spend?.tokens !== null && row.spend?.tokens !== undefined ? (
          <>
            <p className="font-semibold text-body text-text tabular-nums">
              {compactTokens(row.spend.tokens)}
            </p>
            <p className="text-small text-text-tertiary tabular-nums">
              {t("cr")} {formatShare(row.spend.cacheReadShare)}
              {row.spend.amplification ? ` · ${row.spend.amplification}×` : ""}
              {row.spend.budget ? ` · ${t("cap")} ${compactTokens(row.spend.budget)}` : ""}
            </p>
          </>
        ) : row.spend?.budget ? (
          <p className="text-small text-text-tertiary tabular-nums">
            {t("cap")} {compactTokens(row.spend.budget)}
          </p>
        ) : (
          <span className="text-text-tertiary">—</span>
        )}
      </td>
      <td className="px-3 py-2.5">
        {row.target?.ideSessionKey ? (
          <>
            <Text variant="body">{row.target.machine ?? row.target.ideSessionKey}</Text>
            <p className="text-small text-text-tertiary">
              {row.target.workspace ?? "—"}
              {row.target.agent ? ` · ${row.target.agent}` : ""}
              {row.target.telemetry === "partial" ? (
                <span
                  className="italic"
                  title={t(
                    "Copilot has no transcript, so its tokens are not observable. They are excluded from statistics rather than reported as zero.",
                  )}
                >
                  {" "}
                  · {t("partial telemetry")}
                </span>
              ) : null}
            </p>
          </>
        ) : (
          <span className="text-text-tertiary">—</span>
        )}
      </td>
      <td className="px-3 py-2.5">
        {row.progress ? (
          <>
            <Text variant="body">
              {row.progress.nodesDone}/{row.progress.nodesTotal}
            </Text>
            {row.progress.cursorNode ? (
              <p className="text-small text-text-tertiary">{row.progress.cursorNode}</p>
            ) : null}
          </>
        ) : (
          <span className="text-text-tertiary">—</span>
        )}
      </td>
      <td className="px-3 py-2.5">{renderActions()}</td>
    </tr>
  );
}

function TaskTable({
  rows,
  runsById,
  onOpenLedger,
  approvalBusyId,
  onApprove,
}: {
  rows: TaskRow[];
  runsById: Map<string, Run>;
  onOpenLedger: (row: TaskRow) => void;
  approvalBusyId: string | null;
  onApprove: (run: Run, approved: boolean) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="overflow-x-auto rounded-md border border-divider">
      <table className="w-full text-start text-body">
        <thead>
          <tr className="border-b border-divider text-small text-text-tertiary">
            <th className="px-3 py-2 text-start font-medium">{t("Task")}</th>
            <th className="px-3 py-2 text-start font-medium">{t("State")}</th>
            <th className="px-3 py-2 text-start font-medium">{t("Health")}</th>
            <th className="px-3 py-2 text-start font-medium">{t("Spend")}</th>
            <th className="px-3 py-2 text-start font-medium">{t("Target")}</th>
            <th className="px-3 py-2 text-start font-medium">{t("Progress")}</th>
            <th className="px-3 py-2 text-start font-medium">{t("Actions")}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <TaskTableRow
              key={rowKey(row)}
              row={row}
              runDto={
                row.source === "run" ? runsById.get(row.id) : runsById.get(row.links.run ?? "")
              }
              onOpenLedger={onOpenLedger}
              approvalBusyId={approvalBusyId}
              onApprove={onApprove}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Tasks — the machinery front door. One surface over everything that runs: scheduled
 * triggers, live and recent runs, and dispatches queued for an IDE machine. Health rolls up
 * *infrastructure*, not just task state, so a task that is active and nonetheless dead says so.
 */
export function Tasks() {
  const { t, i18n } = useTranslation();
  const { data: board, isLoading } = useTaskBoard();
  // Approval gates ride the run DTO (`awaitingApprovalNodeId`), which tasks.list does not
  // project — poll the active runs alongside so "Needs you" can carry Approve/Deny.
  const { data: activeRuns } = useRuns({ activeOnly: true, limit: 100 });
  const approve = useApproveRun();

  const [ledgerRunId, setLedgerRunId] = useState<string | null>(null);
  const [ledgerOpen, setLedgerOpen] = useState(false);
  const [ledgerTitle, setLedgerTitle] = useState<string | undefined>();
  const [sourceFilter, setSourceFilter] = useState<"" | TaskRow["source"]>("");
  const [approvalBusyId, setApprovalBusyId] = useState<string | null>(null);

  const runsById = useMemo(() => {
    const map = new Map<string, Run>();
    for (const run of activeRuns ?? []) {
      map.set(run.id, run);
    }
    return map;
  }, [activeRuns]);

  const tasks = board?.tasks ?? [];
  const workerAlive = board?.infrastructure.workerProcessingJobs !== false;

  const openLedger = (row: TaskRow) => {
    const runId = row.source === "run" ? row.id : (row.links.run ?? null);
    setLedgerRunId(runId);
    setLedgerTitle(row.title);
    setLedgerOpen(true);
  };

  const handleApprove = (run: Run, approved: boolean) => {
    setApprovalBusyId(run.id);
    approve.mutate(
      { id: run.id, decision: { nodeId: run.awaitingApprovalNodeId ?? "", approved } },
      {
        onSuccess: () => toast.success(approved ? t("Node approved.") : t("Node denied.")),
        onError: () =>
          toast.error(approved ? t("Could not approve the node.") : t("Could not deny the node.")),
        onSettled: () => setApprovalBusyId(null),
      },
    );
  };

  const rows = sourceFilter ? tasks.filter((row) => row.source === sourceFilter) : tasks;

  const liveCount = tasks.filter(
    (row) => row.source !== "scheduled" && LIVE_STATES.has(row.state),
  ).length;
  const liveTokens = tasks.reduce((sum, row) => sum + (row.spend?.tokens ?? 0), 0);

  const needsYou = useMemo(() => {
    const rank = (level: "ok" | "warn" | "error") =>
      level === "error" ? 0 : level === "warn" ? 1 : 2;
    const unhealthy = tasks
      .filter((row) => row.health.level !== "ok")
      .sort((a, b) => rank(a.health.level) - rank(b.health.level));
    const seen = new Set(unhealthy.map(rowKey));
    const approvals = tasks.filter((row) => {
      if (seen.has(rowKey(row))) {
        return false;
      }
      const run = row.source === "run" ? runsById.get(row.id) : runsById.get(row.links.run ?? "");
      return Boolean(run?.awaitingApprovalNodeId);
    });
    return [...approvals, ...unhealthy];
  }, [tasks, runsById]);

  const changed = useMemo(
    () =>
      tasks
        .filter((row) => row.lastActionAt || row.schedule?.lastFiredAt)
        .sort((a, b) => {
          const at = a.lastActionAt ?? a.schedule?.lastFiredAt ?? "";
          const bt = b.lastActionAt ?? b.schedule?.lastFiredAt ?? "";
          return bt.localeCompare(at);
        })
        .slice(0, 5),
    [tasks],
  );

  const upcoming = useMemo(() => {
    const horizon = Date.now() + 24 * 3600_000;
    return tasks
      .filter(
        (row) =>
          row.state === "active" &&
          row.schedule?.nextFireAt &&
          new Date(row.schedule.nextFireAt).getTime() <= horizon,
      )
      .sort(
        (a, b) =>
          new Date(a.schedule?.nextFireAt as string).getTime() -
          new Date(b.schedule?.nextFireAt as string).getTime(),
      );
  }, [tasks]);

  if (isLoading) {
    return <Spinner label={t("Loading")} />;
  }

  return (
    <div className="flex flex-col gap-5">
      <Text variant="small">
        {t(
          "Everything the machinery is doing: scheduled tasks, live and recent runs, and work dispatched to an IDE machine. Health rolls up infrastructure, so a task that is active and nonetheless dead says so.",
        )}
      </Text>

      {/* Band 1: data health — shown only when something is wrong. */}
      {board && (board.worstHealth !== "ok" || !workerAlive) ? (
        <BandStrip level={board.worstHealth}>
          <p className="font-semibold text-body text-text">
            {workerAlive
              ? t("Something needs attention")
              : t("knowit-worker is not processing scheduled jobs")}
          </p>
          {board.warnings.map((warning) => (
            <p key={warning} className="text-body text-text">
              {warning}
            </p>
          ))}
          <p className="mt-1 text-small text-text-tertiary">
            {t("live IDE sessions")}: {board.infrastructure.liveIdeSessions} · {t("checked")}{" "}
            {formatRelativeTime(board.checkedAt, i18n.language)}
          </p>
        </BandStrip>
      ) : null}

      {/* Band 2: summary stats — the four cold answers this screen exists to give. */}
      <div className="flex flex-wrap gap-4 rounded-md border border-divider px-4 py-3">
        <SummaryStat label={t("Running now")} value={String(liveCount)} />
        <SummaryStat label={t("Tokens on the board")} value={compactTokens(liveTokens)} />
        <SummaryStat
          label={t("Needs you")}
          value={String(needsYou.length)}
          tone={needsYou.length > 0 ? "danger" : "neutral"}
        />
        <SummaryStat
          label={t("Worker")}
          value={workerAlive ? t("processing") : t("stopped")}
          tone={workerAlive ? "neutral" : "danger"}
        />
        <div className="flex flex-col gap-0.5">
          <span className="text-small text-text-secondary">{t("Command ledger")}</span>
          <Button
            size="sm"
            onClick={() => {
              setLedgerRunId(null);
              setLedgerTitle(undefined);
              setLedgerOpen(true);
            }}
          >
            {t("Open")}
          </Button>
        </div>
      </div>

      {/* Band 3: needs you. */}
      {needsYou.length > 0 ? (
        <div className="flex flex-col gap-2">
          <h3 className="text-body font-medium text-text">{t("Needs you")}</h3>
          <TaskTable
            rows={needsYou}
            runsById={runsById}
            onOpenLedger={openLedger}
            approvalBusyId={approvalBusyId}
            onApprove={handleApprove}
          />
        </div>
      ) : null}

      {/* Band 4: what changed. */}
      {changed.length > 0 ? (
        <div className="flex flex-col gap-2">
          <h3 className="text-body font-medium text-text">{t("What changed")}</h3>
          <ul className="flex flex-col gap-1 ps-4">
            {changed.map((row) => (
              <li key={rowKey(row)} className="list-disc text-body text-text">
                <span className="font-semibold">{row.title}</span>{" "}
                <span className="text-text-secondary">
                  {row.state} ·{" "}
                  {formatRelativeTime(
                    (row.lastActionAt ?? row.schedule?.lastFiredAt) as string,
                    i18n.language,
                  )}
                  {row.spend?.tokens ? ` · ${compactTokens(row.spend.tokens)}` : ""}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {/* Band 5: everything. */}
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-3">
          <h3 className="text-body font-medium text-text">{t("Everything")}</h3>
          <Select
            aria-label={t("Filter by source")}
            value={sourceFilter}
            onChange={(event) => setSourceFilter(event.target.value as "" | TaskRow["source"])}
          >
            <option value="">{t("All sources")}</option>
            <option value="scheduled">{t("Scheduled")}</option>
            <option value="run">{t("Runs")}</option>
            <option value="dispatch">{t("Dispatches")}</option>
          </Select>
        </div>
        {rows.length === 0 ? (
          <Empty message={t("Nothing is scheduled, running or dispatched.")} />
        ) : (
          <TaskTable
            rows={rows}
            runsById={runsById}
            onOpenLedger={openLedger}
            approvalBusyId={approvalBusyId}
            onApprove={handleApprove}
          />
        )}
      </div>

      {/* Band 6: next 24 hours. */}
      <div className="flex flex-col gap-2">
        <h3 className="text-body font-medium text-text">{t("Next 24 hours")}</h3>
        {upcoming.length === 0 ? (
          <Empty message={t("Nothing is due to fire in the next 24 hours.")} />
        ) : (
          <div className="flex gap-3 overflow-x-auto pb-2">
            {upcoming.map((row) => (
              <div
                key={rowKey(row)}
                className={cn(
                  "min-w-48 shrink-0 rounded-md border px-3 py-2.5",
                  row.health.level === "error" ? "border-danger" : "border-divider",
                )}
              >
                <p className="text-small text-text-tertiary">
                  {formatRelativeTime(row.schedule?.nextFireAt as string, i18n.language)}
                </p>
                <p className="mt-0.5 font-semibold text-body text-text">{row.title}</p>
                {row.health.level !== "ok" ? (
                  <p className="mt-1 text-small text-danger">{row.health.note}</p>
                ) : null}
              </div>
            ))}
          </div>
        )}
      </div>

      {ledgerOpen ? (
        <ActionLedger
          runId={ledgerRunId}
          title={ledgerTitle}
          open={ledgerOpen}
          onClose={() => setLedgerOpen(false)}
        />
      ) : null}
    </div>
  );
}
